<script setup>
import { ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import EventActivityItem from './EventActivityItem.vue';
import Icon from 'next/icon/Icon.vue';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { getDateTimeFormatPattern } from 'shared/helpers/formatTime';
import {
  activityIconFor,
  formatAuditChangeEntries,
} from 'dashboard/helper/planningEventAuditHistoryHelper';

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  historyLoading: { type: Boolean, default: false },
  historyError: { type: String, default: '' },
  historyRecords: { type: Array, default: () => [] },
  historyMeta: {
    type: Object,
    default: () => ({ currentPage: 1, perPage: 20, totalEntries: 0 }),
  },
  getAuditChanges: { type: Function, default: null },
  actionLabel: { type: Function, default: null },
  actionBadgeClass: { type: Function, default: null },
});

const emit = defineEmits(['close', 'pageChange', 'update:isOpen']);
const { t } = useI18n();
const dialogRef = ref(null);

const changesForAudit = audit => {
  if (typeof props.getAuditChanges === 'function') {
    return props.getAuditChanges(audit);
  }
  return formatAuditChangeEntries(audit, { includeActor: true });
};

const iconForAudit = audit => {
  const changes = changesForAudit(audit);
  const iconKey = changes[0]?.icon || 'general';
  return activityIconFor(iconKey);
};

const openDialog = async () => {
  await nextTick();
  // Defer past the click that opened us (header menu / sidebar), otherwise
  // floating-ui click-outside + aria-hidden focus fights the modal.
  await new Promise(resolve => {
    window.setTimeout(resolve, 0);
  });
  dialogRef.value?.open?.();
};

watch(
  () => props.isOpen,
  open => {
    if (open) {
      openDialog();
    } else if (dialogRef.value) {
      dialogRef.value.close?.();
    }
  }
);

const tableHeaders = [
  t('PLANNING.SHOW.HISTORY_TABLE.WHEN'),
  t('PLANNING.SHOW.HISTORY_TABLE.CHANGES'),
];

const handleClose = () => {
  emit('update:isOpen', false);
  emit('close');
};

defineExpose({
  open: openDialog,
  close: () => dialogRef.value?.close(),
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    class="print:hidden"
    :title="t('PLANNING.SHOW.HISTORY_TITLE')"
    :show-confirm-button="false"
    :show-cancel-button="false"
    width="5xl"
    overflow-y-auto
    @close="handleClose"
  >
    <div class="min-w-full text-n-slate-11">
      <div v-if="historyLoading" class="py-8 flex justify-center">
        <woot-loading-state :message="t('PLANNING.SHOW.HISTORY_LOADING')" />
      </div>
      <div
        v-else-if="historyError"
        class="py-8 text-center text-sm text-red-600"
      >
        {{ historyError }}
      </div>
      <p
        v-else-if="!historyRecords.length"
        class="flex flex-col items-center justify-center py-8 text-sm"
      >
        {{ t('PLANNING.SHOW.HISTORY_EMPTY') }}
      </p>
      <div v-else class="min-w-full overflow-x-auto">
        <table class="min-w-full divide-y divide-n-weak">
          <thead>
            <tr>
              <th class="py-3 w-12 ltr:pr-2 rtl:pl-2" aria-hidden="true" />
              <th
                v-for="header in tableHeaders"
                :key="header"
                class="py-3 ltr:pr-4 rtl:pl-4 text-left font-semibold text-n-slate-11 text-sm"
              >
                {{ header }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak text-n-slate-11">
            <tr v-for="audit in historyRecords" :key="audit.id">
              <td class="py-3 ltr:pr-2 rtl:pl-2 align-top w-12">
                <div
                  class="flex size-8 items-center justify-center rounded-full"
                  :class="iconForAudit(audit).className"
                >
                  <Icon :icon="iconForAudit(audit).icon" class="size-3.5" />
                </div>
              </td>
              <td
                class="py-3 ltr:pr-4 rtl:pl-4 whitespace-nowrap text-sm align-top"
              >
                {{
                  messageTimestamp(
                    audit.created_at,
                    getDateTimeFormatPattern('MMM dd, yyyy')
                  )
                }}
              </td>
              <td class="py-3 ltr:pr-4 rtl:pl-4 text-sm align-top min-w-[18rem]">
                <p
                  v-if="!changesForAudit(audit).length"
                  class="mb-0 text-n-slate-10"
                >
                  {{ t('PLANNING.SHOW.HISTORY_NO_DIFF') }}
                </p>
                <ul v-else class="mb-0 space-y-3 list-none pl-0">
                  <li
                    v-for="(entry, index) in changesForAudit(audit)"
                    :key="`${audit.id}-${entry.field}-${index}`"
                  >
                    <EventActivityItem
                      :item="entry"
                      show-actor
                      :show-icon="false"
                      compact
                    />
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
        <TableFooter
          :current-page="Number(historyMeta.currentPage)"
          :total-count="historyMeta.totalEntries"
          :page-size="historyMeta.perPage"
          class="border-n-weak border-t !px-0 pt-4"
          @page-change="emit('pageChange', $event)"
        />
      </div>
    </div>
  </Dialog>
</template>
